import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, ClipboardCheck, BookOpen, Clock, Trophy, Target,
  Trash2, Edit, Play, Users, ChevronRight, Search,
  Loader2, CheckCircle2, XCircle, HelpCircle, ToggleLeft, ToggleRight,
  AlertTriangle, Shield, TrendingUp, UserPlus, Calendar, Eye,
  ChevronDown, Filter, X, Library, ArrowRight, RefreshCw, Share2, BarChart3
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Progress } from "../../components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../../components/ui/sheet";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import ShareDialog from "../../components/shared/ShareDialog";

const STATUS_COLORS = {
  completed: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
  pending: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
  overdue: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200" },
};

const RECOMMENDATION_BADGES = {
  mandatory: { label: "Mandatory", className: "bg-red-50 text-red-700 border-red-200" },
  recommended: { label: "Recommended", className: "bg-blue-50 text-blue-700 border-blue-200" },
  industry_standard: { label: "Industry Standard", className: "bg-green-50 text-green-700 border-green-200" },
};

const AssessmentsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const basePath = user?.role === "manager" ? "/manager" : user?.role === "tenant_admin" ? "/tenant" : "/admin";
  const [assessments, setAssessments] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [library, setLibrary] = useState([]);
  const [courses, setCourses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("assessments");
  const [search, setSearch] = useState("");

  // Create/Edit assessment state
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [form, setForm] = useState({
    title: "", description: "", course_id: "", passing_score: 70,
    time_limit_minutes: 30, max_attempts: 3, shuffle_questions: true, questions: []
  });
  const [qForm, setQForm] = useState({
    question_text: "", question_type: "mcq", options: ["", "", "", ""],
    correct_answer: "", points: 1, explanation: ""
  });

  // Assign modal state
  const [showAssign, setShowAssign] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignUserIds, setAssignUserIds] = useState([]);
  const [assignDueDate, setAssignDueDate] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [assignSearch, setAssignSearch] = useState("");
  const [activating, setActivating] = useState(null);
  const [drawerAssessment, setDrawerAssessment] = useState(null);
  const [drawerSearch, setDrawerSearch] = useState("");

  // Assignment filters
  const [assignFilterStatus, setAssignFilterStatus] = useState("all");
  const [assignFilterAssessment, setAssignFilterAssessment] = useState("all");

  // Detail/standing view
  const [expandedAssessment, setExpandedAssessment] = useState(null);
  const [shareAssessment, setShareAssessment] = useState(null);

  const variant = user?.role === "super_admin" ? "super_admin" : "tenant_admin";

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, cRes, assignRes, libRes] = await Promise.all([
        api.get("/assessments"),
        api.get("/courses"),
        api.get("/assessments/assignments"),
        api.get("/assessments/library"),
      ]);
      setAssessments(aRes.data || []);
      setCourses(cRes.data || []);
      setAssignments(assignRes.data || []);
      setLibrary(libRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get("/users");
      setUsers((res.data?.users || res.data || []).filter(u => u.role !== "super_admin"));
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { fetchData(); fetchUsers(); }, [fetchData, fetchUsers]);

  // ── Assessment CRUD ──
  const handleSave = async () => {
    if (!form.title) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      if (editMode && selectedAssessment) {
        await api.put(`/assessments/${selectedAssessment.id}`, form);
        toast.success("Assessment updated");
      } else {
        await api.post("/assessments", form);
        toast.success("Assessment created");
      }
      setShowCreate(false); resetForm(); fetchData();
    } catch (e) { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this assessment and all its assignments?")) return;
    try { await api.delete(`/assessments/${id}`); toast.success("Deleted"); fetchData(); }
    catch (e) { toast.error("Failed"); }
  };

  const handlePublish = async (a) => {
    try {
      await api.put(`/assessments/${a.id}`, { is_published: !a.is_published });
      toast.success(a.is_published ? "Unpublished" : "Published");
      fetchData();
    } catch (e) { toast.error("Failed"); }
  };

  const handleActivateLibrary = async (libId) => {
    setActivating(libId);
    try {
      const res = await api.post(`/assessments/library/${libId}/activate`);
      toast.success(res.data?.message || "Activated");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to activate");
    }
    setActivating(null);
  };

  const resetForm = () => {
    setForm({ title: "", description: "", course_id: "", passing_score: 70, time_limit_minutes: 30, max_attempts: 3, shuffle_questions: true, questions: [] });
    setEditMode(false); setSelectedAssessment(null);
  };

  const openEdit = (a) => {
    setEditMode(true); setSelectedAssessment(a);
    setForm({
      title: a.title, description: a.description || "", course_id: a.course_id || "",
      passing_score: a.passing_score, time_limit_minutes: a.time_limit_minutes,
      max_attempts: a.max_attempts, shuffle_questions: a.shuffle_questions, questions: a.questions || []
    });
    setShowCreate(true);
  };

  const addQuestion = () => {
    const q = {
      id: `q${Date.now()}`, question_text: qForm.question_text,
      question_type: qForm.question_type,
      options: qForm.question_type === "fill_in" ? [] : qForm.options.filter(o => o.trim()),
      correct_answer: qForm.correct_answer.toLowerCase().trim(),
      points: qForm.points, explanation: qForm.explanation
    };
    setForm({ ...form, questions: [...form.questions, q] });
    setQForm({ question_text: "", question_type: "mcq", options: ["", "", "", ""], correct_answer: "", points: 1, explanation: "" });
  };

  const removeQuestion = (idx) => {
    setForm({ ...form, questions: form.questions.filter((_, i) => i !== idx) });
  };

  // ── Assignment ──
  const openAssignModal = (assessment) => {
    setAssignTarget(assessment);
    setAssignUserIds([]);
    setAssignDueDate(new Date(Date.now() + 30 * 86400000).toISOString().split("T")[0]);
    setAssignSearch("");
    setShowAssign(true);
  };

  const handleAssign = async () => {
    if (!assignTarget || assignUserIds.length === 0) { toast.error("Select at least one user"); return; }
    setAssigning(true);
    try {
      const res = await api.post(`/assessments/${assignTarget.id}/assign`, {
        user_ids: assignUserIds, due_date: assignDueDate,
      });
      toast.success(res.data?.message || "Assigned");
      setShowAssign(false); fetchData();
    } catch (e) { toast.error("Assignment failed"); }
    finally { setAssigning(false); }
  };

  const handleRemoveAssignment = async (assignmentId) => {
    try {
      await api.delete(`/assessments/assignments/${assignmentId}`);
      toast.success("Assignment removed"); fetchData();
    } catch (e) { toast.error("Failed to remove"); }
  };

  const toggleUserSelection = (uid) => {
    setAssignUserIds(prev => prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]);
  };

  const selectAllFiltered = () => {
    const filtered = users.filter(u => !assignSearch || u.full_name?.toLowerCase().includes(assignSearch.toLowerCase()) || u.email?.toLowerCase().includes(assignSearch.toLowerCase()));
    setAssignUserIds(filtered.map(u => u.id));
  };

  // ── Computed ──
  const totalAssigned = assignments.length;
  const completedCount = assignments.filter(a => a.status === "completed").length;
  const overdueCount = assignments.filter(a => a.status === "overdue").length;
  const pendingCount = assignments.filter(a => a.status === "pending").length;
  const completionRate = totalAssigned > 0 ? Math.round((completedCount / totalAssigned) * 100) : 0;

  const getAssessmentStats = (assessId) => {
    const aa = assignments.filter(a => a.assessment_id === assessId);
    const completed = aa.filter(a => a.status === "completed").length;
    const overdue = aa.filter(a => a.status === "overdue").length;
    const total = aa.length;
    const rate = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { total, completed, overdue, rate, assignments: aa };
  };

  const filteredAssignments = assignments.filter(a => {
    if (assignFilterStatus !== "all" && a.status !== assignFilterStatus) return false;
    if (assignFilterAssessment !== "all" && a.assessment_id !== assignFilterAssessment) return false;
    return true;
  });

  const filteredLibrary = useMemo(() => {
    if (!search.trim()) return library;
    const q = search.toLowerCase();
    return library.filter(t => t.title.toLowerCase().includes(q) || (t.description || "").toLowerCase().includes(q));
  }, [library, search]);

  const activatedTitles = useMemo(() => new Set(assessments.map(a => a.title)), [assessments]);

  const typeIcons = { mcq: HelpCircle, true_false: CheckCircle2, fill_in: Edit };

  if (loading) return (
    <AdminLayout title="Assessments" variant={variant}>
      <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
    </AdminLayout>
  );

  return (
    <AdminLayout title="Assessments" variant={variant}>
      <div className="space-y-5" data-testid="assessments-page">

        {/* Dashboard Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="border shadow-none lg:col-span-2">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-bold tracking-tight">Assessment Program</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">Track assessment completions across your organization</p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-black text-primary">{completionRate}%</p>
                  <p className="text-[10px] text-muted-foreground">Completion Rate</p>
                </div>
              </div>
              <Progress value={completionRate} className="h-3 mb-3" />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                {[
                  { label: "Total Assessments", value: assessments.length, icon: ClipboardCheck, color: "text-indigo-600", bg: "bg-indigo-50" },
                  { label: "Completed", value: completedCount, icon: CheckCircle2, color: "text-green-600", bg: "bg-green-50" },
                  { label: "Pending", value: pendingCount, icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
                  { label: "Overdue", value: overdueCount, icon: AlertTriangle, color: "text-red-600", bg: "bg-red-50" },
                ].map((st, i) => (
                  <div key={i} className={`flex items-center gap-2.5 p-2.5 rounded-lg ${st.bg}`}>
                    <st.icon className={`w-4 h-4 ${st.color} shrink-0`} />
                    <div>
                      <p className="text-lg font-bold leading-none">{st.value}</p>
                      <p className="text-[10px] text-muted-foreground">{st.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border shadow-none">
            <CardHeader className="pb-2 pt-4 px-5">
              <CardTitle className="text-sm flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-primary" /> Assessment Metrics
              </CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-4 space-y-2.5">
              {assessments.slice(0, 4).map(a => {
                const stats = getAssessmentStats(a.id);
                return (
                  <div key={a.id} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium truncate max-w-[140px]">{a.title}</span>
                      <div className="flex items-center gap-2">
                        {stats.overdue > 0 && <Badge className="bg-red-50 text-red-600 text-[9px] px-1.5 py-0">{stats.overdue} overdue</Badge>}
                        <span className="text-xs font-bold">{stats.rate}%</span>
                      </div>
                    </div>
                    <Progress value={stats.rate} className="h-1.5" />
                    <p className="text-[10px] text-muted-foreground">{stats.completed}/{stats.total} completed</p>
                  </div>
                );
              })}
              {assessments.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No assessments yet</p>}
            </CardContent>
          </Card>
        </div>

        {/* Tabs + Search + Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
            <TabsList className="bg-muted/50 h-9">
              <TabsTrigger value="assessments" className="text-xs gap-1.5" data-testid="tab-assessments-list">
                <ClipboardCheck className="w-3.5 h-3.5" /> Assessments
                <Badge variant="secondary" className="ml-1 text-[9px] px-1.5 py-0 h-4">{assessments.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="assignments" className="text-xs gap-1.5" data-testid="tab-assignments">
                <Users className="w-3.5 h-3.5" /> Assignments
                <Badge variant="secondary" className="ml-1 text-[9px] px-1.5 py-0 h-4">{totalAssigned}</Badge>
              </TabsTrigger>
              <TabsTrigger value="completed" className="text-xs gap-1.5" data-testid="tab-completed">
                <CheckCircle2 className="w-3.5 h-3.5" /> Completed
                <Badge variant="secondary" className="ml-1 text-[9px] px-1.5 py-0 h-4">{completedCount}</Badge>
              </TabsTrigger>
              <TabsTrigger value="overdue" className="text-xs gap-1.5" data-testid="tab-overdue">
                <AlertTriangle className="w-3.5 h-3.5" /> Overdue
                <Badge variant="secondary" className="ml-1 text-[9px] px-1.5 py-0 h-4">{overdueCount}</Badge>
              </TabsTrigger>
              <TabsTrigger value="library" className="text-xs gap-1.5" data-testid="tab-library">
                <Library className="w-3.5 h-3.5" /> Library
                <Badge variant="secondary" className="ml-1 text-[9px] px-1.5 py-0 h-4">{library.length}</Badge>
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search assessments..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9 w-56 text-sm" data-testid="search-input" />
            </div>
            <Button onClick={() => { resetForm(); setShowCreate(true); }} className="h-9 text-xs gap-1.5" data-testid="create-assessment-btn">
              <Plus className="w-4 h-4" /> New Assessment
            </Button>
          </div>
        </div>

        {/* ═══ LIBRARY TAB ═══ */}
        {activeTab === "library" ? (
          <div className="space-y-3" data-testid="library-section">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold">Pre-Made Assessment Templates</h3>
                <p className="text-xs text-muted-foreground">Industry-standard assessments ready to activate for your organization</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <AnimatePresence>
                {filteredLibrary.map(t => {
                  const isActivated = activatedTitles.has(t.title);
                  const recBadge = RECOMMENDATION_BADGES[t.recommendation_type];
                  return (
                    <motion.div key={t.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                      <Card className={`border shadow-none transition-all hover:shadow-sm ${isActivated ? "opacity-60 border-green-200" : "hover:border-primary/30"}`} data-testid={`lib-card-${t.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${isActivated ? "bg-green-100" : "bg-indigo-50"}`}>
                              {isActivated ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <ClipboardCheck className="w-5 h-5 text-indigo-500" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="text-sm font-semibold truncate">{t.title}</h4>
                                {recBadge && <Badge className={`text-[9px] px-1.5 py-0 border ${recBadge.className}`}>{recBadge.label}</Badge>}
                              </div>
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.description}</p>
                              <div className="flex items-center gap-3 mt-2.5">
                                {t.category && <Badge variant="outline" className="text-[9px]">{t.category}</Badge>}
                                <span className="text-[10px] text-muted-foreground flex items-center gap-1"><HelpCircle className="w-3 h-3" /> {(t.questions || []).length} questions</span>
                                <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> {t.time_limit_minutes}min</span>
                                <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Target className="w-3 h-3" /> Pass: {t.passing_score}%</span>
                              </div>
                              {t.industry_tags?.length > 0 && (
                                <div className="flex items-center gap-1 mt-2">
                                  {t.industry_tags.map(tag => (
                                    <Badge key={tag} variant="outline" className="text-[8px] px-1 py-0 capitalize">{tag.replace("_", " ")}</Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="mt-3 pt-3 border-t flex justify-end">
                            {isActivated ? (
                              <Badge className="bg-green-50 text-green-600 border-green-200 text-xs">Already Active</Badge>
                            ) : (
                              <Button size="sm" className="text-xs h-8 gap-1" onClick={() => handleActivateLibrary(t.id)} disabled={activating === t.id} data-testid={`activate-${t.id}`}>
                                {activating === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
                                Activate for Org
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
              {filteredLibrary.length === 0 && (
                <div className="col-span-full text-center py-10">
                  <Library className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-sm text-muted-foreground">No assessment templates found</p>
                </div>
              )}
            </div>
          </div>
        ) : activeTab === "assessments" ? (
          /* ═══ ASSESSMENTS TAB ═══ */
          <div className="space-y-3" data-testid="assessments-list">
            <AnimatePresence>
              {assessments.length === 0 ? (
                <div className="text-center py-12">
                  <ClipboardCheck className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-sm text-muted-foreground">No assessments yet. Create one or activate from the Library.</p>
                </div>
              ) : assessments.map(a => {
                const stats = getAssessmentStats(a.id);
                const isExpanded = expandedAssessment === a.id;
                const recBadge = RECOMMENDATION_BADGES[a.recommendation_type];
                return (
                  <motion.div key={a.id} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}>
                    <Card
                      className={`border shadow-none transition-all hover:shadow-sm group cursor-pointer ${
                        stats.rate === 100 && stats.total > 0 ? "border-green-200 bg-green-50/20" :
                        stats.overdue > 0 ? "border-red-200 bg-red-50/10" :
                        "hover:border-primary/30"
                      }`}
                      data-testid={`assessment-card-${a.id}`}
                      onClick={() => setDrawerAssessment(a)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          {/* Icon */}
                          <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                            stats.rate === 100 && stats.total > 0 ? "bg-green-100" :
                            stats.overdue > 0 ? "bg-red-100" : "bg-indigo-50"
                          }`}>
                            {stats.rate === 100 && stats.total > 0 ? <CheckCircle2 className="w-5 h-5 text-green-600" /> :
                             stats.overdue > 0 ? <AlertTriangle className="w-5 h-5 text-red-500" /> :
                             <ClipboardCheck className="w-5 h-5 text-indigo-500" />}
                          </div>
                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <h4 className="text-sm font-semibold group-hover:text-primary transition-colors">{a.title}</h4>
                              <Badge variant={a.is_published ? "default" : "outline"} className="text-[9px]">{a.is_published ? "Published" : "Draft"}</Badge>
                              {a.category && <Badge variant="outline" className="text-[9px]">{a.category}</Badge>}
                              {recBadge && <Badge className={`text-[9px] px-1.5 py-0 border ${recBadge.className}`}>{recBadge.label}</Badge>}
                            </div>
                            {a.description && <p className="text-xs text-muted-foreground line-clamp-1">{a.description}</p>}
                            <div className="flex items-center gap-4 mt-2">
                              <span className="text-[10px] text-muted-foreground"><HelpCircle className="w-3 h-3 inline mr-0.5" />{a.questions?.length || 0} questions</span>
                              <span className="text-[10px] text-muted-foreground"><Clock className="w-3 h-3 inline mr-0.5" />{a.time_limit_minutes}min</span>
                              <span className="text-[10px] text-muted-foreground"><Target className="w-3 h-3 inline mr-0.5" />Pass: {a.passing_score}%</span>
                              {stats.total > 0 && <span className="text-[10px] text-muted-foreground"><Users className="w-3 h-3 inline mr-0.5" />{stats.total} assigned</span>}
                            </div>
                          </div>
                          {/* Progress + Actions */}
                          <div className="flex items-center gap-4 shrink-0">
                            {stats.total > 0 && (
                              <div className="text-right hidden sm:block">
                                <div className="flex items-center gap-3 text-[10px]">
                                  <span className="text-green-600 font-medium">{stats.completed} done</span>
                                  {stats.overdue > 0 && <span className="text-red-600 font-medium">{stats.overdue} overdue</span>}
                                </div>
                                <div className="w-32 mt-1.5"><Progress value={stats.rate} className="h-1.5" /></div>
                              </div>
                            )}
                            {stats.total > 0 && (
                              <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 ${
                                stats.rate === 100 ? "border-green-400 text-green-600" :
                                stats.overdue > 0 ? "border-red-300 text-red-600" :
                                "border-primary/30 text-primary"
                              }`}>
                                <span className="text-sm font-bold">{stats.rate}%</span>
                              </div>
                            )}
                            <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => setShareAssessment(a)} data-testid={`share-btn-${a.id}`} title="Share">
                                <Share2 className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => openAssignModal(a)} data-testid={`assign-btn-${a.id}`}>
                                <UserPlus className="w-4 h-4" />
                              </Button>
                              {stats.total > 0 && (
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setExpandedAssessment(isExpanded ? null : a.id)} data-testid={`expand-btn-${a.id}`}>
                                  <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                                </Button>
                              )}
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handlePublish(a)} data-testid={`publish-btn-${a.id}`}>
                                {a.is_published ? <ToggleRight className="w-5 h-5 text-green-500" /> : <ToggleLeft className="w-5 h-5" />}
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => openEdit(a)} data-testid={`edit-btn-${a.id}`}>
                                <Edit className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-500" onClick={() => handleDelete(a.id)} data-testid={`delete-btn-${a.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                            <ChevronRight className="w-4 h-4 text-muted-foreground/40" />
                          </div>
                        </div>
                        {/* Expanded User Standing */}
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                              <div className="mt-4 pt-4 border-t">
                                <h4 className="text-sm font-semibold mb-3">User Standing</h4>
                                <div className="grid grid-cols-12 gap-2 text-[10px] text-muted-foreground uppercase tracking-wider font-semibold px-2 pb-2 border-b">
                                  <span className="col-span-3">User</span>
                                  <span className="col-span-2">Department</span>
                                  <span className="col-span-2 text-center">Status</span>
                                  <span className="col-span-1 text-center">Attempts</span>
                                  <span className="col-span-2 text-center">Best Score</span>
                                  <span className="col-span-2 text-center">Due Date</span>
                                </div>
                                <div className="max-h-[250px] overflow-y-auto">
                                  {stats.assignments.map(sa => {
                                    const sc = STATUS_COLORS[sa.status] || STATUS_COLORS.pending;
                                    return (
                                      <div key={sa.id} className="grid grid-cols-12 gap-2 items-center py-2 px-2 hover:bg-muted/30 rounded text-sm" data-testid={`standing-row-${sa.id}`}>
                                        <div className="col-span-3 truncate">
                                          <p className="font-medium truncate">{sa.user_name}</p>
                                          <p className="text-[10px] text-muted-foreground truncate">{sa.user_email}</p>
                                        </div>
                                        <span className="col-span-2 text-muted-foreground text-xs truncate">{sa.department || "Unassigned"}</span>
                                        <span className="col-span-2 text-center">
                                          <Badge className={`text-[10px] px-1.5 py-0 ${sc.bg} ${sc.text} ${sc.border} border`}>{sa.status}</Badge>
                                        </span>
                                        <span className="col-span-1 text-center">{sa.attempt_count}</span>
                                        <span className="col-span-2 text-center font-semibold" style={{ color: sa.best_score != null ? (sa.best_score >= (sa.passing_score || 70) ? "#10b981" : "#ef4444") : "#94a3b8" }}>
                                          {sa.best_score != null ? `${sa.best_score}%` : "\u2014"}
                                        </span>
                                        <span className="col-span-2 text-center text-xs text-muted-foreground">{sa.due_date || "\u2014"}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        ) : activeTab === "assignments" ? (
          /* ═══ ASSIGNMENTS TAB ═══ */
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={assignFilterStatus} onValueChange={setAssignFilterStatus}>
                <SelectTrigger className="w-[140px] h-9 text-xs" data-testid="filter-assign-status">
                  <Filter className="w-3.5 h-3.5 mr-1 text-muted-foreground" /><SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                </SelectContent>
              </Select>
              <Select value={assignFilterAssessment} onValueChange={setAssignFilterAssessment}>
                <SelectTrigger className="w-[200px] h-9 text-xs" data-testid="filter-assign-assessment">
                  <ClipboardCheck className="w-3.5 h-3.5 mr-1 text-muted-foreground" /><SelectValue placeholder="Assessment" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Assessments</SelectItem>
                  {assessments.map(a => <SelectItem key={a.id} value={a.id}>{a.title}</SelectItem>)}
                </SelectContent>
              </Select>
              {(assignFilterStatus !== "all" || assignFilterAssessment !== "all") && (
                <Button variant="ghost" size="sm" className="text-xs h-9" onClick={() => { setAssignFilterStatus("all"); setAssignFilterAssessment("all"); }}>
                  <X className="w-3.5 h-3.5 mr-1" /> Clear
                </Button>
              )}
              <span className="text-xs text-muted-foreground ml-auto">{filteredAssignments.length} assignments</span>
            </div>
            <Card className="border shadow-none" data-testid="assignments-table">
              <CardContent className="p-0">
                <div className="grid grid-cols-12 gap-2 text-[10px] text-muted-foreground uppercase tracking-wider font-semibold px-4 py-3 border-b bg-muted/30">
                  <span className="col-span-3">User</span><span className="col-span-3">Assessment</span>
                  <span className="col-span-1 text-center">Status</span><span className="col-span-1 text-center">Attempts</span>
                  <span className="col-span-1 text-center">Best Score</span><span className="col-span-2 text-center">Due Date</span>
                  <span className="col-span-1 text-center">Action</span>
                </div>
                <div className="max-h-[500px] overflow-y-auto">
                  {filteredAssignments.length === 0 ? (
                    <div className="text-center py-12 text-sm text-muted-foreground">No assignments found</div>
                  ) : filteredAssignments.map(a => {
                    const sc = STATUS_COLORS[a.status] || STATUS_COLORS.pending;
                    return (
                      <div key={a.id} className="grid grid-cols-12 gap-2 items-center px-4 py-3 border-b hover:bg-muted/20 transition-colors" data-testid={`assignment-row-${a.id}`}>
                        <div className="col-span-3 min-w-0"><p className="text-sm font-medium truncate">{a.user_name}</p><p className="text-[10px] text-muted-foreground truncate">{a.department || "Unassigned"}</p></div>
                        <div className="col-span-3 min-w-0"><p className="text-sm truncate">{a.assessment_title}</p><p className="text-[10px] text-muted-foreground">{a.question_count} Q · Pass: {a.passing_score}%</p></div>
                        <span className="col-span-1 text-center"><Badge className={`text-[10px] px-1.5 py-0 ${sc.bg} ${sc.text} ${sc.border} border`}>{a.status}</Badge></span>
                        <span className="col-span-1 text-center text-sm">{a.attempt_count}</span>
                        <span className="col-span-1 text-center text-sm font-semibold" style={{ color: a.best_score != null ? (a.passed ? "#10b981" : "#ef4444") : "#94a3b8" }}>{a.best_score != null ? `${a.best_score}%` : "\u2014"}</span>
                        <span className="col-span-2 text-center text-xs text-muted-foreground">{a.due_date || "\u2014"}</span>
                        <span className="col-span-1 text-center">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => handleRemoveAssignment(a.id)} data-testid={`remove-assign-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : activeTab === "completed" ? (
          /* ═══ COMPLETED TAB ═══ */
          <Card className="border shadow-none" data-testid="completed-table">
            <CardContent className="p-0">
              <div className="grid grid-cols-12 gap-2 text-[10px] text-muted-foreground uppercase tracking-wider font-semibold px-4 py-3 border-b bg-muted/30">
                <span className="col-span-3">User</span><span className="col-span-3">Assessment</span>
                <span className="col-span-2 text-center">Score</span><span className="col-span-2 text-center">Attempts</span>
                <span className="col-span-2 text-center">Completed</span>
              </div>
              <div className="max-h-[500px] overflow-y-auto">
                {assignments.filter(a => a.status === "completed").length === 0 ? (
                  <div className="text-center py-12 text-sm text-muted-foreground">No completed assignments yet</div>
                ) : assignments.filter(a => a.status === "completed").map(a => (
                  <div key={a.id} className="grid grid-cols-12 gap-2 items-center px-4 py-3 border-b" data-testid={`completed-row-${a.id}`}>
                    <div className="col-span-3 min-w-0"><p className="text-sm font-medium truncate">{a.user_name}</p><p className="text-[10px] text-muted-foreground truncate">{a.department || "Unassigned"}</p></div>
                    <p className="col-span-3 text-sm truncate">{a.assessment_title}</p>
                    <span className="col-span-2 text-center text-sm font-bold text-emerald-600">{a.best_score != null ? `${a.best_score}%` : "\u2014"}</span>
                    <span className="col-span-2 text-center text-sm">{a.attempt_count}</span>
                    <span className="col-span-2 text-center text-xs text-muted-foreground">{a.completed_at ? new Date(a.completed_at).toLocaleDateString() : "\u2014"}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : (
          /* ═══ OVERDUE TAB ═══ */
          <Card className="border shadow-none" data-testid="overdue-table">
            <CardContent className="p-0">
              <div className="grid grid-cols-12 gap-2 text-[10px] text-muted-foreground uppercase tracking-wider font-semibold px-4 py-3 border-b bg-muted/30">
                <span className="col-span-3">User</span><span className="col-span-3">Assessment</span>
                <span className="col-span-2 text-center">Due Date</span><span className="col-span-2 text-center">Attempts</span>
                <span className="col-span-2 text-center">Action</span>
              </div>
              <div className="max-h-[500px] overflow-y-auto">
                {assignments.filter(a => a.status === "overdue").length === 0 ? (
                  <div className="text-center py-12 text-sm text-muted-foreground">No overdue assignments</div>
                ) : assignments.filter(a => a.status === "overdue").map(a => (
                  <div key={a.id} className="grid grid-cols-12 gap-2 items-center px-4 py-3 border-b bg-red-50/30" data-testid={`overdue-row-${a.id}`}>
                    <div className="col-span-3 min-w-0"><p className="text-sm font-medium truncate">{a.user_name}</p><p className="text-[10px] text-muted-foreground truncate">{a.department || "Unassigned"}</p></div>
                    <p className="col-span-3 text-sm truncate">{a.assessment_title}</p>
                    <span className="col-span-2 text-center text-xs font-medium text-red-600">{a.due_date}</span>
                    <span className="col-span-2 text-center text-sm">{a.attempt_count}</span>
                    <span className="col-span-2 text-center">
                      <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive" onClick={() => handleRemoveAssignment(a.id)}>
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
                      </Button>
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ═══ CREATE/EDIT DIALOG ═══ */}
      <Dialog open={showCreate} onOpenChange={v => { if (!v) resetForm(); setShowCreate(v); }}>
        <DialogContent className="max-w-3xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle>{editMode ? "Edit" : "Create"} Assessment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto max-h-[60vh] pr-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs">Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="h-9 text-sm" data-testid="assessment-title-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Linked Course</Label>
                <Select value={form.course_id || "none"} onValueChange={v => setForm({ ...form, course_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{courses.slice(0, 30).map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className="text-sm" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-1.5"><Label className="text-xs">Passing Score (%)</Label><Input type="number" value={form.passing_score} onChange={e => setForm({ ...form, passing_score: parseInt(e.target.value) || 70 })} className="h-9 text-sm" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Time Limit (min)</Label><Input type="number" value={form.time_limit_minutes} onChange={e => setForm({ ...form, time_limit_minutes: parseInt(e.target.value) || 0 })} className="h-9 text-sm" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Max Attempts</Label><Input type="number" value={form.max_attempts} onChange={e => setForm({ ...form, max_attempts: parseInt(e.target.value) || 3 })} className="h-9 text-sm" /></div>
            </div>
            {/* Questions */}
            <div className="border-t pt-4">
              <h4 className="font-semibold text-sm mb-3">Questions ({form.questions.length})</h4>
              {form.questions.map((q, i) => {
                const QIcon = typeIcons[q.question_type] || HelpCircle;
                return (
                  <div key={i} className="flex items-start gap-2 p-3 rounded-lg border bg-muted/30 mb-2">
                    <QIcon className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{q.question_text}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">{q.question_type.replace("_", " ")}</Badge>
                        <span className="text-xs text-muted-foreground">{q.points}pt</span>
                        <span className="text-xs text-green-600">Answer: {q.correct_answer}</span>
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive shrink-0" onClick={() => removeQuestion(i)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                );
              })}
              <div className="mt-3 p-4 rounded-lg border-2 border-dashed">
                <h5 className="text-sm font-medium mb-3">Add Question</h5>
                <div className="space-y-3">
                  <Input placeholder="Question text..." value={qForm.question_text} onChange={e => setQForm({ ...qForm, question_text: e.target.value })} className="h-9 text-sm" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <Select value={qForm.question_type} onValueChange={v => setQForm({ ...qForm, question_type: v, options: v === "true_false" ? ["True", "False"] : v === "fill_in" ? [] : ["", "", "", ""] })}>
                      <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mcq">Multiple Choice</SelectItem>
                        <SelectItem value="true_false">True/False</SelectItem>
                        <SelectItem value="fill_in">Fill in the Blank</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input type="number" placeholder="Points" value={qForm.points} onChange={e => setQForm({ ...qForm, points: parseInt(e.target.value) || 1 })} className="h-9 text-sm" />
                    <Input placeholder="Correct answer" value={qForm.correct_answer} onChange={e => setQForm({ ...qForm, correct_answer: e.target.value })} className="h-9 text-sm" />
                  </div>
                  {qForm.question_type === "mcq" && (
                    <div className="grid grid-cols-2 gap-2">
                      {qForm.options.map((o, i) => (
                        <Input key={i} placeholder={`Option ${i + 1}`} value={o} onChange={e => { const opts = [...qForm.options]; opts[i] = e.target.value; setQForm({ ...qForm, options: opts }); }} className="h-9 text-sm" />
                      ))}
                    </div>
                  )}
                  <Button size="sm" variant="outline" onClick={addQuestion} disabled={!qForm.question_text || !qForm.correct_answer} data-testid="add-question-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add Question
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => { resetForm(); setShowCreate(false); }}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-assessment-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{editMode ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ ASSIGN DIALOG ═══ */}
      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent className="max-w-lg max-h-[85vh]">
          <DialogHeader>
            <DialogTitle>Assign Assessment</DialogTitle>
            <DialogDescription>Assign "{assignTarget?.title}" to users with a deadline</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Due Date</Label>
              <Input type="date" value={assignDueDate} onChange={e => setAssignDueDate(e.target.value)} className="h-9 text-sm" data-testid="assign-due-date" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs">Select Users ({assignUserIds.length} selected)</Label>
                <Button size="sm" variant="ghost" className="text-xs h-7" onClick={selectAllFiltered} data-testid="select-all-users">Select All</Button>
              </div>
              <Input placeholder="Search users..." value={assignSearch} onChange={e => setAssignSearch(e.target.value)} className="mb-2 h-9 text-sm" data-testid="assign-user-search" />
              <div className="border rounded-lg max-h-[250px] overflow-y-auto">
                {users.filter(u => !assignSearch || u.full_name?.toLowerCase().includes(assignSearch.toLowerCase()) || u.email?.toLowerCase().includes(assignSearch.toLowerCase())).map(u => (
                  <label key={u.id} className="flex items-center gap-3 px-3 py-2 hover:bg-muted/30 cursor-pointer border-b last:border-0" data-testid={`user-option-${u.id}`}>
                    <input type="checkbox" checked={assignUserIds.includes(u.id)} onChange={() => toggleUserSelection(u.id)} className="rounded border-muted-foreground/30" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{u.full_name}</p>
                      <p className="text-[10px] text-muted-foreground">{u.email} · {u.department || "No dept"}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowAssign(false)}>Cancel</Button>
            <Button onClick={handleAssign} disabled={assigning || assignUserIds.length === 0} data-testid="confirm-assign-btn">
              {assigning && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Assign to {assignUserIds.length} User{assignUserIds.length !== 1 ? "s" : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Assessment Dialog */}
      <ShareDialog
        open={!!shareAssessment}
        onOpenChange={(v) => { if (!v) setShareAssessment(null); }}
        itemType="assessment"
        itemId={shareAssessment?.id || ""}
        itemTitle={shareAssessment?.title || ""}
      />

      {/* Assessment Detail Side Drawer */}
      <Sheet open={!!drawerAssessment} onOpenChange={(v) => { if (!v) { setDrawerAssessment(null); setDrawerSearch(""); } }}>
        <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col">
          {drawerAssessment && (() => {
            const a = drawerAssessment;
            const aAssigns = assignments.filter(as => as.assessment_id === a.id);
            const filteredAssigns = aAssigns.filter(as =>
              !drawerSearch || (as.user_name || "").toLowerCase().includes(drawerSearch.toLowerCase()) || (as.user_email || "").toLowerCase().includes(drawerSearch.toLowerCase())
            );
            const unassignedUsers = users.filter(u => !aAssigns.some(as => as.user_id === u.id));
            return (
              <>
                <div className="p-5 border-b">
                  <SheetHeader className="mb-0">
                    <SheetTitle className="text-lg">{a.title}</SheetTitle>
                    <SheetDescription className="text-xs">{a.questions?.length || 0} questions · Pass: {a.passing_score}% · {a.time_limit_minutes}m</SheetDescription>
                  </SheetHeader>
                  <div className="flex items-center gap-2 mt-3">
                    <Button size="sm" variant="outline" onClick={() => { setDrawerAssessment(null); navigate(`${basePath}/assessments/${a.id}`); }} data-testid="drawer-analytics-btn">
                      <BarChart3 className="w-3.5 h-3.5 mr-1" /> View Analytics
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setAssignTarget(a); setAssignUserIds([]); setShowAssign(true); }} data-testid="drawer-assign-btn">
                      <UserPlus className="w-3.5 h-3.5 mr-1" /> Assign
                    </Button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                      <Users className="w-4 h-4 text-primary" /> Assigned Users ({aAssigns.length})
                    </h3>
                  </div>
                  {aAssigns.length > 3 && (
                    <div className="relative mb-3">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input placeholder="Search assigned users..." value={drawerSearch} onChange={e => setDrawerSearch(e.target.value)} className="pl-10 h-8 text-xs" data-testid="drawer-search" />
                    </div>
                  )}
                  {filteredAssigns.length === 0 ? (
                    <div className="text-center py-8">
                      <Users className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
                      <p className="text-xs text-muted-foreground">{aAssigns.length === 0 ? "No users assigned yet" : "No users match your search"}</p>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {filteredAssigns.map(as => (
                        <div key={as.id} className="flex items-center gap-3 p-2.5 rounded-lg border hover:bg-muted/30 transition-colors" data-testid={`drawer-user-${as.user_id}`}>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${as.status === "completed" || as.status === "passed" ? "bg-emerald-100 text-emerald-700" : as.status === "failed" ? "bg-red-100 text-red-700" : "bg-primary/10 text-primary"}`}>
                            {(as.user_name || "?").charAt(0)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{as.user_name || as.user_id}</p>
                            <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                              {as.user_email && <span>{as.user_email}</span>}
                              {as.best_score > 0 && <span>Score: {as.best_score}%</span>}
                              <span>{as.attempt_count || 0} attempt{(as.attempt_count || 0) !== 1 ? "s" : ""}</span>
                            </div>
                          </div>
                          <Badge variant="outline" className={`text-[9px] shrink-0 ${as.status === "passed" || as.status === "completed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : as.status === "failed" ? "bg-red-50 text-red-700 border-red-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
                            {as.status || "pending"}
                          </Badge>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-red-500 shrink-0" title="Remove assignment"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await api.delete(`/assessments/assignments/${as.id}`);
                                toast.success("User removed from assessment");
                                fetchData();
                                setDrawerAssessment(prev => prev ? {...prev} : null);
                              } catch (err) { toast.error("Failed to remove assignment"); }
                            }}
                            data-testid={`drawer-remove-${as.user_id}`}>
                            <XCircle className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </SheetContent>
      </Sheet>
    </AdminLayout>
  );
};

export default AssessmentsPage;
